// Extensie is verplicht: package.json staat op "type": "module", dus .js-bestanden
// worden als strikte ESM behandeld.
import {
    collection, doc, getDocs, query, serverTimestamp, setDoc, where,
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { SEASON_STATUS } from './seasons.js';

export const DEFAULT_START_BUDGET = 100;

/**
 * De speler-id's die in dit seizoen aan minstens één veiling (subleague) zijn
 * toegevoegd. Elke veiling is een subleague; een speler zit in een veiling als er
 * een document `auctions/{auctionId}/auctionPlayers/{playerId}` bestaat.
 *
 * Er is geen aggregatie in de database, dus we lopen de veilingen van het seizoen
 * langs en verzamelen de doc-id's van hun `auctionPlayers`. Alleen een globale
 * beheerder mag de auctionPlayers van elke veiling lezen (firestore.rules), dus
 * dit is bedoeld voor het beheerdersscherm Stamgegevens.
 *
 * @param {string|number} seasonId
 * @returns {Promise<Set<string>>} Set van speler-id's (string).
 */
export async function fetchAuctionPlayerIdsForSeason(seasonId) {
    if (!seasonId) return new Set();

    const auctionsSnap = await getDocs(
        query(collection(db, 'auctions'), where('season', '==', String(seasonId))),
    );

    const ids = new Set();
    await Promise.all(auctionsSnap.docs.map(async (auctionDoc) => {
        const spelersSnap = await getDocs(collection(db, 'auctions', auctionDoc.id, 'auctionPlayers'));
        spelersSnap.forEach((d) => ids.add(d.id));
    }));

    return ids;
}

/**
 * De veilingen van een gebruiker, opgesplitst in beheerd en deelgenomen, met de
 * seizoensnaam erbij. Veilingen van een afgerond seizoen vallen weg: die horen niet
 * meer in het overzicht ("Mijn Veilingen") of in de routering van "Ga naar spel".
 *
 * @param {string} uid
 * @returns {Promise<{managed: Array<object>, participating: Array<object>}>}
 */
export async function fetchActiveUserAuctions(uid) {
    if (!uid) return { managed: [], participating: [] };

    const seasonsSnap = await getDocs(collection(db, 'seasons'));
    const seasons = new Map();
    seasonsSnap.forEach((d) => seasons.set(d.id, d.data()));

    const isActive = (auction) => seasons.get(String(auction.season))?.status !== SEASON_STATUS.AFGEROND;
    const withSeasonName = (d) => {
        const data = d.data();
        return {
            id: d.id,
            ...data,
            seasonName: seasons.get(String(data.season))?.name || 'Onbekend seizoen',
        };
    };

    const [managedSnap, participatingSnap] = await Promise.all([
        getDocs(query(collection(db, 'auctions'), where('adminId', '==', uid))),
        getDocs(query(collection(db, 'auctions'), where('participantUids', 'array-contains', uid))),
    ]);

    return {
        managed: managedSnap.docs.map(withSeasonName).filter(isActive),
        participating: participatingSnap.docs.map(withSeasonName).filter(isActive),
    };
}

/**
 * Maakt een subleague (veiling) aan.
 *
 * Gedeeld door twee ingangen die hetzelfde document moeten schrijven:
 * - /start-auction, waar een gebruiker zelf een veiling begint en zelf beheerder wordt;
 * - /admin/season/:seasonId/subleagues, waar de administrator er een aanmaakt en een
 *   ander als beheerder aanwijst.
 *
 * firestore.rules eist bij create dat adminId gelijk is aan de ingelogde gebruiker en
 * dat season een niet-lege string is; season ligt daarna vast.
 *
 * @param {object} params
 * @param {string} params.name          Naam van de subleague.
 * @param {string} params.seasonId      Document-id van het seizoen (Sportmonks season-id).
 * @param {object} params.admin         De beheerder: { uid, email, name }.
 * @param {boolean} [params.isOpen]     Open veiling (iedereen mag meedoen).
 * @param {boolean} [params.addAdminAsParticipant] Beheerder ook als deelnemer opnemen.
 * @returns {Promise<string>} het id van de nieuwe veiling
 */
export async function createAuction({
    name,
    seasonId,
    admin,
    isOpen = true,
    addAdminAsParticipant = true,
}) {
    if (!name?.trim()) throw new Error('Geef de subleague een naam.');
    if (!seasonId) throw new Error('Kies een seizoen.');
    if (!admin?.uid) throw new Error('Wijs een beheerder aan.');

    const auctionRef = doc(collection(db, 'auctions'));

    await setDoc(auctionRef, {
        name: name.trim(),
        isOpen,
        adminId: admin.uid,
        participantUids: addAdminAsParticipant ? [admin.uid] : [],
        createdAt: serverTimestamp(),
        gameType: 'champions-league',
        startBudget: DEFAULT_START_BUDGET,
        season: String(seasonId),
    });

    if (addAdminAsParticipant) {
        await setDoc(doc(db, 'auctions', auctionRef.id, 'participants', admin.uid), {
            uid: admin.uid,
            email: admin.email,
            name: admin.name || admin.email,
            budget: DEFAULT_START_BUDGET,
        });
    }

    return auctionRef.id;
}
